import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { css } from "@emotion/react";
import { Modal, Input } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import { useAuthInfo } from "@sharpana/auth/authHooks";
import { ManageSubscription } from "./Settings";
import {
  request,
  URLs,
  logout as allauthLogout,
  getAuth,
} from "@sharpana/auth/api_client/allauth";

const containerStyles = css`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background-color: #141414;
  color: #ffffff;
  padding: 24px;
`;

const cardStyles = css`
  width: 100%;
  max-width: 560px;
  padding: 24px;
  border-radius: 14px;
  background: linear-gradient(180deg, #181818, #121212);
  border: 1px solid rgba(255, 255, 255, 0.06);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.35),
    inset 0 1px 0 rgba(255, 255, 255, 0.03);
`;

const sectionStyles = css`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const labelStyles = css`
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.95rem;
`;

const valueStyles = css`
  color: #fff;
  font-size: 1.05rem;
  font-weight: 600;
`;

const buttonStyles = css`
  padding: 12px 24px;
  font-size: 1rem;
  font-weight: 600;
  background-color: rgb(157, 60, 231);
  color: #ffffff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  &:hover {
    background-color: rgb(137, 52, 202);
  }
  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(157, 60, 231, 0.4);
  }
`;

const dangerButtonStyles = css`
  padding: 12px 24px;
  font-size: 1rem;
  font-weight: 700;
  background-color: #ef4444; /* red-500 */
  color: #ffffff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  &:hover {
    background-color: #dc2626;
  }
  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.4);
  }
`;

const linkStyles = css`
  color: #a855f7;
  text-decoration: none;
  font-weight: 600;
  margin-bottom: 16px;
  &:hover {
    text-decoration: underline;
  }
`;

// Reusable dark modal styles for AntD v5 `styles` prop
const darkModalStyles = {
  content: {
    background: "linear-gradient(180deg, #1b1b1b, #141414)",
    border: "1px solid rgba(255, 255, 255, 0.08)",
    boxShadow:
      "0 10px 30px rgba(0, 0, 0, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.03)",
  },
  body: {
    color: "#fff",
  },
  mask: {
    backgroundColor: "rgba(0, 0, 0, 0.65)",
    backdropFilter: "blur(1px)",
  },
} as const;

// Dark styled input wrapper for confirm delete flow (targets AntD input internals)
const darkInputStyles = css`
  /* AntD v5 token overrides scoped to this wrapper */
  --ant-color-bg-container: #0f0f0f;
  --ant-color-text: #ffffff;
  --ant-color-text-placeholder: rgba(255, 255, 255, 0.5);
  --ant-color-border: rgba(255, 255, 255, 0.16);
  --ant-color-primary: #a855f7;

  .ant-input,
  .ant-input-affix-wrapper,
  .ant-input-affix-wrapper .ant-input {
    background: #0f0f0f !important;
    color: #ffffff !important;
    border-color: rgba(255, 255, 255, 0.16) !important;
  }

  .ant-input::placeholder {
    color: rgba(255, 255, 255, 0.5) !important;
  }

  .ant-input:hover,
  .ant-input-affix-wrapper:hover {
    border-color: rgba(168, 85, 247, 0.7) !important; /* purple */
  }

  .ant-input:focus,
  .ant-input-focused,
  .ant-input-affix-wrapper-focused {
    border-color: rgba(168, 85, 247, 0.9) !important;
    box-shadow: 0 0 0 2px rgba(168, 85, 247, 0.25) !important;
  }
  /* Increase specificity in case of theme overrides */
  :where(.ant-input) {
    background: #0f0f0f !important;
    color: #ffffff !important;
    border-color: rgba(255, 255, 255, 0.16) !important;
  }
`;

const legalLinksStyles = css`
  margin-top: 16px;
  color: rgba(255, 255, 255, 0.55);
  font-size: 0.85rem;
  text-align: center;

  a {
    color: rgba(255, 255, 255, 0.7);
    text-decoration: none;
  }
  a:hover {
    text-decoration: underline;
    color: #e5e7eb;
  }
  .dot {
    margin: 0 8px;
    color: rgba(255, 255, 255, 0.35);
  }
`;

export default function UserAccount() {
  const navigate = useNavigate();
  const { user } = useAuthInfo();

  const email = useMemo(() => user?.email ?? "", [user]);

  const [showActiveSubModal, setShowActiveSubModal] = useState(false);
  const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false);
  const [confirmText, setConfirmText] = useState("");

  const handleDeleteClick = async () => {
    try {
      const resp: any = await request(
        "GET",
        URLs.HAS_ACTIVE_SUBSCRIPTION_FOR_DELETION
      );
      const hasActive = !!resp?.has_active_subscription;
      if (hasActive) {
        setShowActiveSubModal(true);
      } else {
        setShowConfirmDeleteModal(true);
      }
    } catch (e) {
      // If error, be conservative and show active subscription modal to avoid accidental deletions
      setShowActiveSubModal(true);
    }
  };

  const handleConfirmDeletion = async () => {
    try {
      const resp: any = await request("POST", URLs.DELETE_ACCOUNT);
      setShowConfirmDeleteModal(false);
      if (resp?.success) {
        // eslint-disable-next-line no-alert
        alert("Your account has been successfully deleted");
        // Ensure SPA auth state updates immediately: attempt logout, then force auth refresh
        try {
          await allauthLogout();
        } catch (_) {
          // ignore; session may already be invalidated server-side
        }
        try {
          await getAuth();
        } catch (_) {
          // ignore
        }
        navigate("/", { replace: true });
      } else {
        // eslint-disable-next-line no-alert
        alert(
          "There was an issue submitting your deletion request. Please try again later."
        );
      }
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(
        "There was an error contacting the server. Please try again later."
      );
    }
  };

  return (
    <div css={containerStyles}>
      <a
        href="#"
        onClick={(e) => {
          e.preventDefault();
          navigate("/settings");
        }}
        css={linkStyles}
      >
        ← Back to Settings
      </a>
      <div css={cardStyles}>
        <div css={sectionStyles}>
          <div>
            <div css={labelStyles}>Email</div>
            <div css={valueStyles}>{email || "Unknown"}</div>
          </div>

          <div style={{ height: 8 }} />

          <div>
            <div css={labelStyles}>Danger zone</div>
            <button css={dangerButtonStyles} onClick={handleDeleteClick}>
              Delete account
            </button>
          </div>
        </div>
      </div>

      <div css={legalLinksStyles}>
        <a
          href="https://terms.mrble.ai"
          target="_blank"
          rel="noopener noreferrer"
        >
          Terms of Service
        </a>
        <span className="dot">•</span>
        <a
          href="https://policy.mrble.ai"
          target="_blank"
          rel="noopener noreferrer"
        >
          Privacy Policy
        </a>
      </div>

      {/* Modal: Has active subscription */}
      <Modal
        title={null}
        open={showActiveSubModal}
        onCancel={() => setShowActiveSubModal(false)}
        footer={null}
        centered
        maskClosable
        closable
        closeIcon={
          <CloseOutlined style={{ color: "rgba(255, 255, 255, 0.85)" }} />
        }
        styles={darkModalStyles}
        className={"manage-subscription-modal"}
        width={420}
      >
        <div
          css={css`
            color: #fff;
            display: flex;
            flex-direction: column;
            gap: 16px;
          `}
        >
          <h3
            css={css`
              margin: 0;
              font-size: 1.25rem;
              font-weight: 700;
              color: #fff;
            `}
          >
            Active subscription detected
          </h3>
          <p
            css={css`
              color: #e0e0e0;
              line-height: 1.6;
            `}
          >
            Please cancel your subscription before deleting your account. If you
            are having issues canceling your subscription, contact us at{" "}
            <a
              href="mailto:support@mrble.ai"
              css={css`
                color: #a855f7;
              `}
            >
              support@mrble.ai
            </a>
            .
          </p>
          <div>
            <ManageSubscription label="Manage subscription" />
          </div>
        </div>
      </Modal>

      {/* Modal: Confirm deletion */}
      <Modal
        title={null}
        open={showConfirmDeleteModal}
        onCancel={() => setShowConfirmDeleteModal(false)}
        footer={null}
        centered
        maskClosable
        closable
        closeIcon={
          <CloseOutlined style={{ color: "rgba(255, 255, 255, 0.85)" }} />
        }
        styles={darkModalStyles}
        className={"confirm-delete-modal"}
        width={480}
      >
        <div
          css={css`
            color: #fff;
            display: flex;
            flex-direction: column;
            gap: 16px;
          `}
        >
          <h3
            css={css`
              margin: 0;
              font-size: 1.25rem;
              font-weight: 700;
              color: #fff;
            `}
          >
            Confirm account deletion
          </h3>
          <p
            css={css`
              color: #e0e0e0;
              line-height: 1.6;
            `}
          >
            This action is irreversible. Type <strong>delete</strong> to confirm
            you want to permanently delete your account and all associated data.
          </p>
          <div css={darkInputStyles}>
            <Input
              placeholder="Type delete to confirm"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
            />
          </div>
          <div
            css={css`
              display: flex;
              gap: 12px;
              justify-content: flex-end;
            `}
          >
            <button
              css={buttonStyles}
              onClick={() => setShowConfirmDeleteModal(false)}
            >
              Cancel
            </button>
            <button
              css={dangerButtonStyles}
              onClick={handleConfirmDeletion}
              disabled={confirmText.trim().toLowerCase() !== "delete"}
            >
              Permanently delete
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
